import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "@/components/ui/resizable"
import { Button } from "@/components/ui/button"
import { AlertCircleIcon, ArrowLeft, X } from "lucide-react"
import * as React from "react"
import { useContext, useEffect, useState } from "react"
import { APIProvider } from "@vis.gl/react-google-maps"
import { RotatingTextShimmer } from "@/components/ui/rotating-text-shimmer"
import { Alert, AlertDescription } from "../ui/alert"
import { AlertTitle } from "@/components/ui/alert"
import {
  AnalyzeOrderVerifyStops,
  OrderMetaData,
} from "@/components/maps/AnalyzeOrderVerifyStops"
import { RouteStop } from "@/reducers/routes2gSlice"
import VerifyOrderSkeleton from "@/components/maps/VerifyOrderSkeleton"
import { Spinner } from "@/components/ui/spinner"
import { UploadedFile } from "@/hooks/useExtractDataFromOrder"
import { isConvertibleOrderFile } from "@/lib/orderFileSupport"
import OrderMarkdownPreview from "@/components/maps/OrderMarkdownPreview"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { useNaviConfig } from "@/hooks/useNaviConfig"
import { NaviSelectionInput } from "@/api/adHocCommands"
import { useAnalyzeOrderContextOptional } from "@/contexts/AnalyzeOrderContext"
import { computeLoadedKm } from "@/lib/loadedKm"
import { RouteStopWithHandle } from "@/lib/planRouteParser"
import { XmppContext } from "@/stream/xmppClient"
import { extractVhostFromUser } from "@/jidUtils"
import { useAppSelector } from "@/reducers/hooks"
import { selectEffectiveDriverForChat } from "@/reducers/driverResolutionSelectors"
import { selectAccountChatByJid } from "@/reducers/accountChatsSlice"
import { selectCarById } from "@/reducers/carsSlice"
import { findProfile } from "@/reducers/profilesSliceSelectors"
import { lookupTrailer } from "@/lib/tractorTrailerMap"
import { useTranslation } from "react-i18next"

type AnalyzeOrderVerifyProps = {
  onCloseFullScreen: () => void
  routeMetaData?: any
  stops: RouteStop[] | undefined
  files: UploadedFile[] | null
  isLoading?: boolean
  isFailed?: boolean
  onRetryProcessing?: () => void
  isHidden?: boolean
  isRetrying?: boolean
  onContinue: (data: OrderMetaData, shouldFetch?: boolean) => void
  isFetchingPolylines: boolean
  isFetchingPolylinesFailed: boolean
  polylineErrors?: string[] | null
  onClearPolylineErrors?: () => void
  isRouteFetched: boolean
  onEdited: () => void
  onResetMessageGenerated: () => void
  showSkipStepButton?: boolean
  messageId?: string | null
  continueLabel?: string
  // On-page ("Plan route on map") flow: there is no AnalyzeOrderContext/driver
  // chat, so the parent supplies the Marcos order state setters (and optionally
  // the pre-selected driver's JID) directly. When set and Marcos is configured,
  // the verify step shows the NaviOrderPanel and creates the transport order in
  // the TMS on "Generate route" — same as the driver-chat flow.
  planOrderNavi?: {
    driverJid?: string
    setNaviTransportId: (id: number | undefined) => void
    setNaviSelection: (s: NaviSelectionInput | undefined) => void
  }
}

const AnalyzeOrderVerify: React.FC<AnalyzeOrderVerifyProps> = ({
  onCloseFullScreen,
  routeMetaData,
  isLoading = false,
  isFailed = false,
  files,
  onRetryProcessing,
  isHidden = false,
  isRetrying = false,
  onContinue,
  isFetchingPolylines,
  isFetchingPolylinesFailed,
  polylineErrors,
  onClearPolylineErrors,
  isRouteFetched,
  onEdited,
  onResetMessageGenerated,
  stops,
  showSkipStepButton,
  messageId = null,
  continueLabel,
  planOrderNavi,
}: AnalyzeOrderVerifyProps) => {
  const { t } = useTranslation()
  const [isContentReady, setIsContentReady] = useState<boolean>(false)
  const [selectedFileIndex, setSelectedFileIndex] = useState(0)
  // Full-panel overlay while a Marcos order is being created + the route is
  // generated. Set true when the submit starts; cleared on error. On success it
  // stays until this whole step unmounts (we navigate to the map).
  const [orderSubmitting, setOrderSubmitting] = useState(false)

  // Navi integration
  const { myJid } = useContext(XmppContext)
  let vhost = ""
  try {
    vhost = extractVhostFromUser(myJid?.toString())
  } catch (e) {
    console.error(e)
  }
  const { configured: naviConfigured } = useNaviConfig(vhost)
  const analyzeOrderCtx = useAnalyzeOrderContextOptional()
  // Chat flow: the order is tied to the driver chat from the context. On-page
  // flow: no chat exists — fall back to the driver picked in DriverSelect (may
  // be absent; the dispatcher then picks everything in the Marcos panel).
  const routeJid = analyzeOrderCtx?.routeJid ?? planOrderNavi?.driverJid
  const setNaviTransportId =
    analyzeOrderCtx?.setNaviTransportId ?? planOrderNavi?.setNaviTransportId
  const setNaviSelection =
    analyzeOrderCtx?.setNaviSelection ?? planOrderNavi?.setNaviSelection
  // The chat flow needs a driver chat to hang the order on; the on-page flow
  // only needs its setters.
  const naviReady = analyzeOrderCtx ? Boolean(routeJid) : Boolean(planOrderNavi)

  // Crew details for pre-selecting the Marcos driver/tractor. Same sources as
  // the chat header / Edit crew modal: the effective (driverA-aware) driver's
  // phone, and the crew car's registration (plates, falling back to its label,
  // then the driver's own plates).
  const effectiveDriver = useAppSelector(selectEffectiveDriverForChat(routeJid))
  const accountChat = useAppSelector(selectAccountChatByJid(routeJid ?? ""))
  const crewCar = useAppSelector(selectCarById(accountChat?.carId))
  const driverPhone = effectiveDriver?.phone
  const driverFirstName = effectiveDriver?.firstName
  const driverLastName = effectiveDriver?.lastName
  const truckPlate =
    crewCar?.plates || crewCar?.label || effectiveDriver?.plates
  const trailerPlate = truckPlate ? lookupTrailer(truckPlate) : undefined

  // Logged-in dispatcher, to default-select the supervisor dropdowns. Read from
  // the self vCard (best-effort; may be absent until the profile sync lands).
  const selfProfile = useAppSelector(
    findProfile(myJid?.bare().toString() ?? ""),
  )
  // The self vCard often has no first/last name (e.g. Renata Petrak's is
  // empty), so fall back to the JID local part, which follows a
  // "first_last" convention (e.g. "renata_petrak"). Split on "_"/"." and take
  // the first segment as the first name, the last as the last name.
  const jidNameParts = (myJid?.local ?? "")
    .split(/[._]/)
    .map((s) => s.trim())
    .filter(Boolean)
  const dispatcherFirstName =
    selfProfile?.firstName || jidNameParts[0] || undefined
  const dispatcherLastName =
    selfProfile?.lastName ||
    (jidNameParts.length > 1
      ? jidNameParts[jidNameParts.length - 1]
      : undefined)

  // TEMP debug: why the supervisor auto-select may not fire.
  console.log("[navi-debug] self", {
    myJid: myJid?.bare().toString(),
    selfProfileFound: Boolean(selfProfile),
    selfProfile,
    jidNameParts,
    dispatcherFirstName,
    dispatcherLastName,
  })

  // Reset content state when retry starts
  useEffect(() => {
    if (isRetrying) {
      setIsContentReady(false)
    }
  }, [isRetrying])

  // Update route data when it becomes available
  useEffect(() => {
    if (!isLoading && routeMetaData) {
      setIsContentReady(true)
    } else if (isFailed) {
      window.analytics.track("AnalyzeOrderFailed", {
        reason: "processing_failed",
        files: files?.map((uploadedFile) => uploadedFile.name),
        filesUrls: files?.map((uploadedFile) => uploadedFile.url),
        messageId: messageId,
      })
      setIsContentReady(true)
    }
  }, [isLoading, isFailed, files, onRetryProcessing, routeMetaData, messageId])

  const inProduct = Boolean(window.XMPP && !window.anonymousXMPP)

  return (
    <APIProvider apiKey={import.meta.env.REACT_APP_GOOGLE_MAP_KEY}>
      <div
        className={`${inProduct ? "left-[49px] w-[calc(100vw-3rem)]" : ""} bg:white fixed inset-0 z-[100] h-screen w-screen dark:bg-gray-900 ${isHidden ? "hidden" : ""}`}
      >
        <ResizablePanelGroup
          direction="horizontal"
          className="max-w-screen min-h-screen"
        >
          <ResizablePanel
            className="relative flex flex-col bg-sidebar"
            defaultSize={35}
            maxSize={50}
            minSize={25}
          >
            {orderSubmitting && (
              <div className="absolute inset-0 z-[1003] flex flex-col items-center justify-center gap-3 bg-black/40 backdrop-blur-[1px]">
                <Spinner size="lg" className="text-white" />
                <p className="text-base font-medium text-white">
                  {t("orders.analyzeOrderVerify.creatingInMarcos")}
                </p>
              </div>
            )}
            <div className="flex w-full flex-col p-4 pb-2">
              <div className="flex items-center justify-between">
                {!inProduct && (
                  <Button
                    variant="ghost"
                    onClick={onCloseFullScreen}
                    className="self-start pl-0 text-sm"
                  >
                    <ArrowLeft className="h-4 w-4" />
                    {t("common.back")}
                  </Button>
                )}
                {files && files.length > 1 && (
                  <div className="min-w-[150px]">
                    <Select
                      value={selectedFileIndex.toString()}
                      onValueChange={(value) =>
                        setSelectedFileIndex(parseInt(value))
                      }
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue
                          placeholder={t(
                            "orders.analyzeOrderVerify.selectFile",
                          )}
                        />
                      </SelectTrigger>
                      <SelectContent>
                        {files.map((file, index) => (
                          <SelectItem key={index} value={index.toString()}>
                            {file.name ||
                              t("orders.analyzeOrderVerify.fileFallback", {
                                number: index + 1,
                              })}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
              <h2 className="mb-1 mt-4 font-semibold text-muted-foreground">
                {t("orders.analyzeOrderVerify.step")}
              </h2>
              <h1 className="text-2xl font-bold">
                {t("orders.analyzeOrderVerify.title")}
              </h1>
            </div>
            <div className="relative z-10 flex w-full flex-1 flex-col overflow-hidden bg-sidebar">
              {!isLoading && isFailed && !isFetchingPolylinesFailed ? (
                <div className="flex w-full flex-col p-4">
                  <Alert variant="destructive">
                    <AlertCircleIcon />
                    <AlertTitle>
                      {t("orders.analyzeOrderVerify.processFailed.title")}
                    </AlertTitle>
                    <AlertDescription>
                      <p>
                        {t(
                          "orders.analyzeOrderVerify.processFailed.description",
                        )}
                      </p>
                      <ul className="list-inside list-disc text-sm">
                        <li>
                          {t(
                            "orders.analyzeOrderVerify.processFailed.checkContent",
                          )}
                        </li>
                        <li>
                          {t(
                            "orders.analyzeOrderVerify.processFailed.checkFormat",
                          )}
                        </li>
                      </ul>
                    </AlertDescription>
                  </Alert>
                  <Button
                    className="mt-2 bg-blue-600 px-8 hover:bg-blue-700 dark:text-white"
                    onClick={onRetryProcessing}
                  >
                    {t("common.retry")}
                  </Button>
                </div>
              ) : null}
              {!isLoading && polylineErrors ? (
                <div className="relative flex w-full flex-col p-4">
                  <Alert variant="destructive">
                    <AlertCircleIcon />
                    <AlertTitle>
                      {t("orders.analyzeOrderVerify.routeFetchFailed")}
                    </AlertTitle>
                    <AlertDescription>
                      {polylineErrors.map((msg, i) => (
                        <p key={i}>{msg}</p>
                      ))}
                    </AlertDescription>
                  </Alert>
                  {onClearPolylineErrors && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="absolute right-5 top-5 h-6 w-6 text-destructive hover:text-destructive"
                      onClick={onClearPolylineErrors}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              ) : null}

              {!isContentReady ? (
                <div className="flex h-full w-full flex-col space-y-4 py-4">
                  <RotatingTextShimmer
                    className="mb-6 px-4"
                    messages={[
                      t("orders.analyzeOrderVerify.loading.analyzing"),
                      t("orders.analyzeOrderVerify.loading.extracting"),
                      t("orders.analyzeOrderVerify.loading.requirements"),
                    ]}
                  />
                  <VerifyOrderSkeleton />
                </div>
              ) : (
                isContentReady &&
                routeMetaData &&
                stops && (
                  <div
                    className="flex h-full w-full flex-col"
                    style={{
                      maxHeight: polylineErrors
                        ? "calc(100% - 100px)"
                        : undefined,
                    }}
                  >
                    <AnalyzeOrderVerifyStops
                      data={routeMetaData}
                      extractedStops={stops}
                      loadedKm={computeLoadedKm(
                        analyzeOrderCtx?.routeData?.stops as
                          | RouteStopWithHandle[]
                          | undefined,
                      )}
                      onContinue={onContinue}
                      onCloseFullScreen={onCloseFullScreen}
                      isFetchingPolylines={isFetchingPolylines}
                      isRouteFetched={isRouteFetched}
                      onEdited={onEdited}
                      onResetMessageGenerated={onResetMessageGenerated}
                      showSkipStepButton={showSkipStepButton}
                      messageId={messageId}
                      continueLabel={continueLabel}
                      navi={
                        naviConfigured &&
                        naviReady &&
                        setNaviTransportId &&
                        setNaviSelection
                          ? {
                              vhost: vhost!,
                              driverJid: routeJid ?? "plan-order",
                              driverPhone,
                              driverFirstName,
                              driverLastName,
                              truckPlate,
                              trailerPlate,
                              dispatcherFirstName,
                              dispatcherLastName,
                              uploadedFile: files?.[0],
                              setNaviTransportId,
                              setNaviSelection,
                              onSubmitStart: () => setOrderSubmitting(true),
                              onSubmitError: () => setOrderSubmitting(false),
                            }
                          : undefined
                      }
                    />
                  </div>
                )
              )}
            </div>
          </ResizablePanel>
          <ResizableHandle withHandle className="z-[1001]" />
          <ResizablePanel
            defaultSize={75}
            className="flex flex-col bg-[hsl(var(--muted-foreground))]"
          >
            {files?.[selectedFileIndex] &&
            isConvertibleOrderFile(files[selectedFileIndex].name) ? (
              <OrderMarkdownPreview file={files[selectedFileIndex]} />
            ) : (
              <iframe
                className="h-full w-full"
                src={`/viewers/web/${files?.[selectedFileIndex]?.url?.toLowerCase().endsWith(".pdf") ? "viewer.html" : "imageViewer.html"}?url=${encodeURIComponent(files?.[selectedFileIndex]?.url ?? "")}`}
              />
            )}
          </ResizablePanel>
        </ResizablePanelGroup>
      </div>
    </APIProvider>
  )
}

export default AnalyzeOrderVerify
